import { defineComponent as _defineComponent } from 'vue'
import {
  IAddressSearchInfo,
  IAddressSearchInfoCollection,
} from "@/domain/entities/address";
import { IResultData } from "@/domain/entities/shared/result";
import { useAddressStore } from "@/stores/addressStore";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import { inject, ref } from "vue";
import SearchInput from "../shared/inputs/SearchInput.vue";
import DOMPurify from "dompurify";


const __default__ = {
  name: "AddressSearch",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  searchKeyword: {
    type: String,
    default: "",
  },
},
  emits: ["onChangedSearchKeyword", "onSelectedAddress", "onSearchResultsChanged"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const addressStore = useAddressStore();

const getTextFocusedKeyword = (text: string) => {
  return text.replace(
    new RegExp(props.searchKeyword, "g"),
    `<span class='matched'>${props.searchKeyword}</span>`
  );
};

const emitOnChangedSearchKeyword = (keyword: string) => {
  emit("onChangedSearchKeyword", keyword);
};

const shouldShowSearchResult = ref(false);
const toggleShowSearchResult = (show: boolean) => {
  shouldShowSearchResult.value = show;
  emit("onSearchResultsChanged", show && addressSearchInfos.value.length > 0);
};

const handleError = (
  title: string,
  result: any,
  errorMessage?: string,
  errorCode?: string
) => {
  showErrorModal(
    title,
    errorMessage ?? result.errorMessage,
    errorCode ?? result.resultCode,
    result.errorTime
  );
};

const timer = ref();
const searchAddressAfterTime = () => {
  if (timer.value) {
    clearTimeout(timer.value);
  }
  timer.value = setTimeout(searchAddress, 300);
};

// 주소 검색하기
const currentPage = ref(1);
const countPerPage = ref(10);
const addressSearchInfos = ref<IAddressSearchInfo[]>([]);
const searchAddress = async () => {
  clearTimeout(timer.value);

  if (props.searchKeyword.length < 2) {
    addressSearchInfos.value = [];
    emit("onSearchResultsChanged", false);
    showToastMessage("검색어는 두글자 이상 입력해주세요");
    return;
  }

  const addressSearchResult = await addressStore.searchAddress(
    props.searchKeyword,
    currentPage.value,
    countPerPage.value
  );

  handleSearchAddressResult(addressSearchResult);
};

const handleSearchAddressResult = (
  searchResult: IResultData<IAddressSearchInfoCollection>
) => {
  // 주소 검색 API가 실패하는 경우 에러 팝업 표출
  if (!searchResult.isSuccess) {
    handleError("주소 검색 오류", searchResult);
    return;
  }

  // API는 성공했으나 응답 데이터에 에러가 포함된 경우 에러 팝업 표출
  if (searchResult.data?.common.errorCode !== "0") {
    const errorMessage = searchResult.data?.common.errorMessage;
    const errorCode = searchResult.data?.common.errorCode;
    handleError("주소 검색 오류", searchResult, errorMessage, errorCode);
    return;
  }

  // 주소 검색 결과가 없는 경우 토스트 메세지 표출
  if (!searchResult.data.infos.length) {
    addressSearchInfos.value = [];
    emit("onSearchResultsChanged", false);
    showToastMessage("검색 결과가 없습니다");
    return;
  }

  addressSearchInfos.value = searchResult.data?.infos ?? [];
  toggleShowSearchResult(true);
};

// 주소 좌표 가져오기(utm-k 좌표계 기준)
const getAddressCoordinate = async (addressInfo: IAddressSearchInfo) => {
  toggleShowSearchResult(false);

  const { dongNo, roadNo, isUnder, buildingMainNo, buildingSubNo } =
    addressInfo;
  const utmkCoordinateResult = await addressStore.getAddressCoordinate(
    dongNo,
    roadNo,
    isUnder,
    buildingMainNo,
    buildingSubNo
  );

  if (!utmkCoordinateResult.isSuccess) {
    handleError("주소 좌표 오류", utmkCoordinateResult);
    return;
  }

  const utmkCoordinateInfo = utmkCoordinateResult.data;
  const coordinateResult = await convertUtmkToLatLng(
    utmkCoordinateInfo?.utmkX,
    utmkCoordinateInfo?.utmkY
  );
  if (!coordinateResult?.data) return;

  const selectedAddress: IAddressSearchInfo = {
    ...addressInfo,
    latitude: coordinateResult.data.latitude,
    longitude: coordinateResult.data.longitude,
  };
  emit("onSelectedAddress", selectedAddress);
};

// 좌표 변환하기(utm-k 좌표계 => 위경도 좌표계)
const convertUtmkToLatLng = async (x?: string, y?: string) => {
  if (!x || !y) return;

  const coordinateResult = await addressStore.convertUtmkToLatLng(x, y);
  if (!coordinateResult.isSuccess) {
    showToastMessage("주소 좌표를 변환하는데 실패하였습니다.");
    return;
  }

  return coordinateResult;
};

__expose({ toggleShowSearchResult });

const __returned__ = { props, emit, showErrorModal, showToastMessage, addressStore, getTextFocusedKeyword, emitOnChangedSearchKeyword, shouldShowSearchResult, toggleShowSearchResult, handleError, timer, searchAddressAfterTime, currentPage, countPerPage, addressSearchInfos, searchAddress, handleSearchAddressResult, getAddressCoordinate, convertUtmkToLatLng, SearchInput, get DOMPurify() { return DOMPurify } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})